import { identityClient, apiClient } from './client';
import {
  LoginRequest,
  TokenRequest,
  TokenResponse,
  SupportProfile,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminForgotPasswordRequest,
  AdminResetPasswordRequest,
  AdminProfile,
} from '@/types';

export const authApi = {
  // Admin email+password login
  adminLogin: async (data: AdminLoginRequest): Promise<AdminLoginResponse> => {
    const response = await identityClient.post<AdminLoginResponse>('/admin/auth/login', data);
    return response.data;
  },

  // Admin refresh token
  adminRefreshToken: async (refreshToken: string): Promise<AdminLoginResponse> => {
    const response = await identityClient.post<AdminLoginResponse>('/admin/auth/refresh', {
      refreshToken,
    });
    return response.data;
  },

  // Admin change password
  adminChangePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await identityClient.post('/admin/auth/change-password', { currentPassword, newPassword });
  },

  // Tenant/SuperAdmin profiles live in Identity, not in Core's Support table.
  getAdminProfile: async (): Promise<AdminProfile> => {
    const response = await identityClient.get<AdminProfile>('/admin/auth/profile');
    return response.data;
  },

  updateAdminProfile: async (data: Pick<AdminProfile, 'fullName' | 'mobileNo'>): Promise<AdminProfile> => {
    const response = await identityClient.put<AdminProfile>('/admin/auth/profile', data);
    return response.data;
  },

  adminLogoutAll: async (): Promise<void> => {
    await identityClient.post('/admin/auth/logout-all');
  },

  adminForgotPassword: async (data: AdminForgotPasswordRequest): Promise<{ message: string }> => {
    const response = await identityClient.post<{ message: string }>('/admin/auth/forgot-password', data);
    return response.data;
  },

  adminResetPassword: async (data: AdminResetPasswordRequest): Promise<{ message: string }> => {
    const response = await identityClient.post<{ message: string }>('/admin/auth/reset-password', data);
    return response.data;
  },

  // Self-serve provider signup creates the pending Identity/Core account.
  // Access tokens are issued only after mailbox verification and a normal
  // admin login, so this endpoint deliberately returns no credentials.
  businessSignup: async (data: BusinessSignupRequest): Promise<BusinessSignupResponse> => {
    const response = await identityClient.post<BusinessSignupResponse>('/auth/business-signup', data);
    return response.data;
  },

  // Phase 3.Z — email verification (non-blocking).
  verifyEmail: async (userId: string, token: string): Promise<{ confirmed?: boolean; alreadyConfirmed?: boolean }> => {
    const response = await identityClient.post<{ confirmed?: boolean; alreadyConfirmed?: boolean }>(
      '/auth/verify-email',
      { userId, token },
    );
    return response.data;
  },

  resendVerification: async (email: string): Promise<{ sent: boolean }> => {
    const response = await identityClient.post<{ sent: boolean }>('/auth/resend-verification', { email });
    return response.data;
  },

  // Request OTP for login
  requestOtp: async (data: LoginRequest): Promise<string> => {
    const response = await identityClient.post<string>('/accounts', data);
    return response.data;
  },

  // Verify OTP and get token
  verifyOtp: async (data: TokenRequest): Promise<TokenResponse> => {
    const response = await identityClient.post<TokenResponse>('/auth', data);
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken: string): Promise<TokenResponse> => {
    const response = await identityClient.post<TokenResponse>('/auth', {
      GrantType: 'refresh_token',
      RefreshToken: refreshToken,
    });
    return response.data;
  },

  // Force regenerate OTP
  forceOtp: async (mobile: string, clientType: string): Promise<string> => {
    const response = await identityClient.post<string>('/accounts/force_otp', null, {
      params: { mobile, clientType },
    });
    return response.data;
  },

  // Reset OTP (support only)
  resetOtp: async (mobileNo: string, clientType: string): Promise<string> => {
    const response = await identityClient.post<string>('/supports/otp', null, {
      params: { mobileNo, clientType },
    });
    return response.data;
  },

  // Get current user profile
  getProfile: async (): Promise<SupportProfile> => {
    const response = await apiClient.get<SupportProfile>('/supports/profile');
    return response.data;
  },

  // Update profile
  updateProfile: async (data: Partial<SupportProfile>): Promise<SupportProfile> => {
    const response = await apiClient.post<SupportProfile>('/supports/profile', data);
    return response.data;
  },
};

export interface BusinessSignupRequest {
  businessName: string;
  email: string;
  password: string;
  mobile: string;
  segment?: 'individual' | 'team' | 'salon' | 'shop';
}

export interface BusinessSignupResponse {
  registrationPending: true;
  emailConfirmationRequired: true;
}
